import { useParams, Navigate } from 'react-router-dom';
import { Header, Footer, PageTransition } from '../components';
import { SEOHead } from '../components/seo';
import { EnhancedImage } from '../components/ui';
import { sectorsData } from '../data/sectors';
import { FaCheckCircle } from 'react-icons/fa';
import { useSpring, animated, useTrail } from 'react-spring';
import { useState, useEffect, useRef } from 'react';
import defaultHeroImage from '../assets/hero_logistics_bw.png';

function SectorDetailPage() {
    const { sectorId } = useParams();
    const sector = sectorId ? sectorsData[sectorId] : null;

    const [heroVisible, setHeroVisible] = useState(false);
    const [contentVisible, setContentVisible] = useState(false);
    
    const heroRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // Intersection Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const target = entry.target;
                        if (target === heroRef.current) setHeroVisible(true);
                        if (target === contentRef.current) setContentVisible(true);
                        observer.unobserve(target);
                    }
                });
            },
            { threshold: 0.15 }
        );

        const refs = [heroRef, contentRef];
        refs.forEach(ref => {
            if (ref.current) observer.observe(ref.current);
        });

        return () => observer.disconnect();
    }, []);

    // Hero animations
    const heroTextAnimation = useSpring({
        opacity: heroVisible ? 1 : 0,
        transform: heroVisible ? 'translateX(0px)' : 'translateX(-50px)',
        config: { mass: 1, tension: 120, friction: 30 },
    });

    const heroImageAnimation = useSpring({
        opacity: heroVisible ? 1 : 0,
        transform: heroVisible ? 'scale(1)' : 'scale(1.1)',
        config: { mass: 1, tension: 120, friction: 30 },
    });

    // Content section animations
    const aboutAnimation = useSpring({
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? 'translateY(0px)' : 'translateY(40px)',
        config: { mass: 1, tension: 120, friction: 30 },
    });

    const solutionsAnimation = useSpring({
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? 'translateY(0px)' : 'translateY(40px)',
        config: { mass: 1, tension: 120, friction: 30 },
        delay: 150,
    });

    const ctaAnimation = useSpring({
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? 'translateX(0px)' : 'translateX(40px)',
        config: { mass: 1, tension: 120, friction: 30 },
        delay: 300,
    });

    const statsAnimation = useSpring({
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? 'scale(1)' : 'scale(0.9)',
        config: { mass: 1, tension: 120, friction: 30 },
        delay: 450,
    });

    if (!sector) {
        return <Navigate to="/not-found" replace />;
    }

    // Features trail animation
    const featuresTrail = useTrail(sector.features.length, {
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? 'translateX(0px)' : 'translateX(-20px)',
        config: { mass: 1, tension: 180, friction: 40 },
    });

    return (
        <div className="min-h-screen bg-white">
            <SEOHead title={`${sector.title} - EasyBuy Sectors`} description={sector.description} />
            <Header />
            <PageTransition>
                <main className="pt-16">
                {/* Split Header */}
                <div className="flex flex-col md:flex-row min-h-[500px] md:h-[70vh]" ref={heroRef}>
                    <animated.div style={heroTextAnimation} className="w-full md:w-1/2 bg-gray-900 text-white flex flex-col justify-center px-6 py-16 md:px-20 md:py-0 relative overflow-hidden">
                        <div className="absolute top-10 left-10 w-20 h-20 border-t-2 border-l-2 border-orange-500/30 hidden md:block"></div>
                        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tighter leading-none mb-6">
                            {sector.title.split(' ')[0]} <br /><span className="text-orange-500">{sector.title.split(' ').slice(1).join(' ') || 'Sector'}</span>
                        </h1>
                        <p className="text-lg md:text-xl text-gray-400 font-light max-w-md border-l-2 border-orange-500 pl-4 md:pl-6">
                            {sector.description}
                        </p>
                    </animated.div>
                    <animated.div style={heroImageAnimation} className="w-full md:w-1/2 relative bg-gray-800 min-h-[300px] md:min-h-0">
                        <div className="absolute inset-0 bg-gray-900/40 mix-blend-multiply z-10"></div>
                        <EnhancedImage
                            src={sector.image || defaultHeroImage}
                            alt={`${sector.title} Overview`}
                            className="w-full h-full grayscale contrast-125"
                            zoomOnHover={true}
                        />
                    </animated.div>
                </div>

                {/* Content Section */}
                <div className="py-12 md:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" ref={contentRef}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start">
                        <div className="space-y-6 md:space-y-8">
                            <animated.div style={aboutAnimation}>
                                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">About this Sector</h2>
                                <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                                    {sector.content}
                                </p>
                            </animated.div>

                            <animated.div style={solutionsAnimation} className="bg-gray-50 p-6 md:p-8 rounded-2xl border border-gray-100">
                                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">Key Solutions</h3>
                                <ul className="space-y-3 md:space-y-4">
                                    {featuresTrail.map((style, idx) => (
                                        <animated.li key={idx} style={style} className="flex items-start gap-3">
                                            <FaCheckCircle className="text-orange-500 mt-1 flex-shrink-0 text-sm md:text-base" />
                                            <span className="text-sm md:text-base text-gray-700 font-medium">{sector.features[idx]}</span>
                                        </animated.li>
                                    ))}
                                </ul>
                            </animated.div>
                        </div>

                        {/* Right Side Visual/CTA */}
                        <div className="space-y-6 md:space-y-8">
                            <animated.div style={ctaAnimation} className="bg-gray-900 rounded-2xl p-6 md:p-10 text-white relative overflow-hidden group">
                                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <h3 className="text-xl md:text-2xl font-bold mb-4 relative z-10">Need supplies for {sector.title}?</h3>
                                <p className="text-sm md:text-base text-gray-300 mb-6 md:mb-8 relative z-10">
                                    Our team specializes in procuring strict requirement lists for the {sector.title} industry. Get a quote today.
                                </p>
                                <a href="/contact" className="inline-block bg-white text-gray-900 font-bold py-3 px-8 rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:translate-x-2 relative z-10">
                                    Request Quote
                                </a>
                            </animated.div>

                            <animated.div style={statsAnimation} className="grid grid-cols-2 gap-4">
                                <div className="bg-orange-50 p-6 rounded-xl text-center">
                                    <div className="text-3xl font-bold text-orange-500 mb-1">Global</div>
                                    <div className="text-sm text-gray-600 font-medium">Sourcing Network</div>
                                </div>
                                <div className="bg-blue-50 p-6 rounded-xl text-center">
                                    <div className="text-3xl font-bold text-blue-600 mb-1">24/7</div>
                                    <div className="text-sm text-gray-600 font-medium">Support Team</div>
                                </div>
                            </animated.div>
                        </div>
                    </div>
                </div>
            </main>
            </PageTransition>
            <Footer />
        </div>
    );
}

export default SectorDetailPage;
